import { FONT_MONO } from '../../config/fonts';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, List, ListItem, TextField,
} from '@mui/material'
import {
  Call,
  CallEnd,
  ChatBubbleOutline,
  ExpandLess,
  ExpandMore,
  Fullscreen,
  FullscreenExit,
  Groups,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  ArrowDropDown,
  Check,
  ScreenShare,
  StopScreenShare,
  CloseFullscreen,
  OpenInFull,
  PanTool,
  ChevronLeft, ChevronRight,
  ClosedCaption, ClosedCaptionOff,
  People, MoreVert, Search, RemoveCircleOutline, AdminPanelSettings,
  FiberManualRecord,
  Lock, ContentCopy
} from '@mui/icons-material'
import { useAuthContext } from '../../context/auth'
import { useCall } from '../../context/call'
import { requestOpenChat } from '../../utils/chatState'
import { avatarColor, avatarSrc, getInitials } from '../../utils/avatar'
import { useUserContext } from '../../context/user'
import { CAPTION_LANGUAGES } from '../../utils/captions'
import { formatPasscode } from '../../utils/e2ee'
import copy from 'copy-to-clipboard'

// The expanded call copies the size and position of the Chat page (see id in Chat.jsx)
const CHAT_ROOT_ID = 'chat-page-root'

// The remote sound always plays through this element, so it keeps playing
// even when the video is hidden or minimized
const RemoteAudio = ({ stream }) => {
  const ref = useRef(null)

  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream || null
  }, [stream])

  return <audio ref={ref} autoPlay />
}

// Picture only (muted on purpose: no echo from the local preview, no double sound)
const VideoEl = ({ stream, style }) => {
  const ref = useRef(null)

  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream || null
  }, [stream])

  return <video ref={ref} autoPlay playsInline muted style={style} />
}

// Small gold badge shown while I am presenting
const PresentingPill = ({ accent }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      px: 0.75,
      py: 0.1,
      borderRadius: '999px',
      fontSize: '0.65rem',
      fontWeight: 700,
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
      color: accent,
      border: `1px solid ${alpha(accent, 0.7)}`,
      backgroundColor: alpha(accent, 0.12),
    }}
  >
    <ScreenShare sx={{ fontSize: '0.8rem' }} />
    Presenting
  </Box>
)

// A LiveKit video track on a <video>. LiveKit only downloads tiles that are actually on screen.
const SfuVideo = ({ track, mirror, fit = 'cover' }) => {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !track) return
    track.attach(el)
    return () => { track.detach(el) }
  }, [track])

  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted
      style={{
        width: '100%',
        height: '100%',
        objectFit: fit,
        transform: mirror ? 'scaleX(-1)' : 'none',
        pointerEvents: 'none',
      }}
    />
  )
}

// One participant in a group call
const GroupTile = ({ name, avatar, stream, track, camera, muted, mirror, expanded, presenting, speaking, hand, note }) => {
  const theme = useTheme()
  const accent = theme.palette.secondary.main

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        backgroundColor: '#1a1a1a',
        ...(expanded ? { minHeight: 0 } : { aspectRatio: '16 / 9' }),
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          borderRadius: 1,
          pointerEvents: 'none',
          border: `3px solid ${accent}`,
          opacity: speaking ? 1 : 0,
          transition: 'opacity 0.15s ease',
        }}
      />
      {camera && (track || stream) ? (
        track ? (
          <SfuVideo track={track} mirror={mirror} fit={expanded ? 'contain' : 'cover'} />
        ) : (
          <VideoEl
            stream={stream}
            style={{
              width: '100%',
              height: '100%',
              objectFit: expanded ? 'contain' : 'cover',
              transform: mirror ? 'scaleX(-1)' : 'none',
              pointerEvents: 'none',
            }}
          />
        )
      ) : (
        <Box height="100%" display="flex" alignItems="center" justifyContent="center">
          <Avatar
            src={avatarSrc(avatar)}
            sx={{
              width: expanded ? 72 : 44,
              height: expanded ? 72 : 44,
              fontSize: expanded ? '2rem' : '1.2rem',
              ...avatarColor(name, theme.palette.mode)
            }}
          >
            {getInitials(name)}
          </Avatar>
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          left: 6,
          bottom: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          backgroundColor: 'rgba(0,0,0,0.55)',
          maxWidth: 'calc(100% - 12px)',
        }}
      >
        {presenting && <ScreenShare sx={{ fontSize: '0.9rem', color: accent }} />}
        {hand && <PanTool sx={{ fontSize: '0.9rem', color: accent }} />}
        {muted && <MicOff sx={{ fontSize: '0.9rem', color: '#ff6b6b' }} />}
        <Typography variant="caption" noWrap sx={{ color: '#ffffff' }}>
          {name}
        </Typography>
        {note && (
          <Typography variant="caption" noWrap sx={{ color: '#bbbbbb' }}>
            · {note}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600)
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')
  const s = String(seconds % 60).padStart(2, '0')
  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`
}

// Large meeting: a paginated grid (never 100 videos at once), a big stage for a presenter,
// and a line showing who has a raised hand
const SfuStage = ({ people, expanded, avatarOf }) => {
  const theme = useTheme()
  const accent = theme.palette.secondary.main
  const [page, setPage] = useState(0)

  // Maximum tiles allowed per view page
  const perPage = expanded ? 12 : 4

  // Me first, then people with their camera on, then sort alphabetically by name
  const ordered = useMemo(
    () =>
      [...people].sort((a, b) => {
        if (a.isLocal !== b.isLocal) return a.isLocal ? -1 : 1
        if (a.camera !== b.camera) return a.camera ? -1 : 1
        return (a.name || '').localeCompare(b.name || '')
      }),
    [people]
  )

  const presenter = people.find((p) => p.screen && !p.isLocal) || null
  const hands = people.filter((p) => p.hand)

  const pages = Math.max(1, Math.ceil(ordered.length / perPage))
  const current = Math.min(page, pages - 1)
  const visible = ordered.slice(current * perPage, (current + 1) * perPage)

  // DYNAMIC GRID CALCULATION: Adjust columns and rows strictly by visible occupant count
  const cols = useMemo(() => {
    if (!expanded) return 2                 // Keep small popup behavior
    if (visible.length === 1) return 1      // 1 Person -> 1 Full column
    if (visible.length === 2) return 2      // 2 People -> 2 Side-by-side columns
    if (visible.length <= 4) return 2       // 3 or 4 People -> 2x2 grid
    if (visible.length <= 6) return 3       // 5 or 6 People -> 3 columns
    return 4                                // 7 or more -> Full 4 columns layout
  }, [visible.length, expanded])

  const rows = Math.max(1, Math.ceil(visible.length / cols))

  const tile = (p, isExpanded) => (
    <GroupTile
      key={p.id}
      name={p.isLocal ? 'You' : p.name}
      avatar={avatarOf?.(p.id)}
      track={p.cameraTrack}
      camera={p.camera}
      muted={!p.mic}
      mirror={p.isLocal}
      presenting={p.screen}
      speaking={p.speaking}
      hand={p.hand}
      expanded={isExpanded}
      note={p.camera ? (p.cameraTrack ? '' : 'loading video…') : 'camera off'}
    />
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 0.5,
        pt: expanded ? 7 : 5.5,                 // room for the header elements
        ...(expanded ? { flex: 1, minHeight: 0 } : {}),
      }}
    >
      {presenter && (
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 1,
            backgroundColor: '#111111',
            ...(expanded ? { flex: 1, minHeight: 0 } : { aspectRatio: '16 / 9' }),
          }}
        >
          <SfuVideo track={presenter.screenTrack} fit="contain" />
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              bottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.9,
              py: 0.3,
              borderRadius: 1,
              backgroundColor: 'rgba(0,0,0,0.6)',
            }}
          >
            <ScreenShare sx={{ fontSize: '1rem', color: accent }} />
            <Typography variant="caption" noWrap sx={{ color: '#ffffff', fontWeight: 600 }}>
              {presenter.name} is presenting
            </Typography>
          </Box>
        </Box>
      )}

      {presenter ? (
        <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', flexShrink: 0 }}>
          {visible.map((p) => (
            <Box key={p.id} sx={{ width: expanded ? 160 : 100, flexShrink: 0 }}>
              {tile(p, false)}
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 0.5,
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            ...(expanded
              ? { flex: 1, minHeight: 0, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }
              : {}),
          }}
        >
          {visible.map((p) => tile(p, expanded))}
        </Box>
      )}

      {(pages > 1 || hands.length > 0) && (
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} px={0.5} sx={{ flexShrink: 0 }}>
          <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
            {hands.length > 0 && (
              <>
                <PanTool sx={{ fontSize: '0.95rem', color: accent }} />
                <Typography variant="caption" noWrap sx={{ color: '#ffffff' }}>
                  {hands.map((p) => (p.isLocal ? 'You' : p.name)).join(', ')}
                </Typography>
              </>
            )}
          </Box>

          {pages > 1 && (
            <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
              <IconButton size="small" disabled={current === 0} onClick={() => setPage(current - 1)} sx={{ color: '#ffffff' }}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ color: '#ffffff', fontVariantNumeric: 'tabular-nums' }}>
                {current + 1} / {pages} · {people.length} people
              </Typography>
              <IconButton size="small" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} sx={{ color: '#ffffff' }}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

// YouTube-style captions: the last two speakers, newest words at the end
const CaptionLines = ({ captions, myId, large }) => {
  const theme = useTheme()
  const accent = theme.palette.secondary.main

  const items = Object.entries(captions)
    .map(([id, c]) => ({ id, ...c }))
    .sort((a, b) => a.at - b.at)
    .slice(-2)

  if (items.length === 0) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
      {items.map((c) => {
        const text = c.text.length > 160 ? `…${c.text.slice(-160)}` : c.text   // keep the END of long sentences

        return (
          <Typography
            key={c.id}
            component="div"
            sx={{
              maxWidth: '100%',
              px: 1.25,
              py: 0.4,
              borderRadius: 1,
              backgroundColor: 'rgba(0,0,0,0.78)',
              color: '#ffffff',
              fontSize: large ? '1.05rem' : '0.82rem',
              lineHeight: 1.35,
              textAlign: 'center',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              opacity: c.final ? 1 : 0.92,
            }}
          >
            <Box component="span" sx={{ color: accent, fontWeight: 700 }}>
              {c.id === myId ? 'You' : c.name}:{' '}
            </Box>
            {text}
          </Typography>
        )
      })}
    </Box>
  )
}

// People in a large meeting. Everybody can search the list; hosts also get the controls.
const ParticipantsDialog = ({ open, onClose, people, hostIds, isHost, avatarOf, onAction, isPrivate, passcode }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const accent = theme.palette.secondary.main
  const accentText = theme.palette.getContrastText(accent)

  const [query, setQuery] = useState('')
  const [menu, setMenu] = useState(null)          // { anchor, person }
  const [confirm, setConfirm] = useState(null)    // person about to be removed
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) {
      setQuery('')
      setMenu(null)
      setConfirm(null)
    }
  }, [open])

  const hostSet = useMemo(() => new Set((hostIds || []).map(String)), [hostIds])

  const list = useMemo(() => {
    const q = query.trim().toLowerCase()

    return [...people]
      .filter((p) => !q || (p.name || '').toLowerCase().includes(q))
      .sort((a, b) => {
        if (Boolean(a.hand) !== Boolean(b.hand)) return a.hand ? -1 : 1            // raised hands first
        const ah = hostSet.has(String(a.id))
        const bh = hostSet.has(String(b.id))
        if (ah !== bh) return ah ? -1 : 1
        if (a.isLocal !== b.isLocal) return a.isLocal ? -1 : 1
        return (a.name || '').localeCompare(b.name || '')
      })
  }, [people, query, hostSet])

  const unmutedOthers = people.filter((p) => !p.isLocal && p.mic && !hostSet.has(String(p.id))).length

  const run = async (action, id) => {
    setBusy(true)
    try {
      return await onAction(action, id)
    } finally {
      setBusy(false)
    }
  }

  const paperSx = {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.text.primary,
    borderRadius: 2,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
    boxShadow: 8,
    overflow: 'hidden',
  }

  const menuPerson = menu?.person
  const menuIsHost = menuPerson ? hostSet.has(String(menuPerson.id)) : false

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        sx={{ zIndex: theme.zIndex.modal + 60 }}          // above the floating call window
        PaperProps={{ sx: paperSx }}
      >
        <DialogTitle sx={{ px: 2, py: 1.5, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
          People ({people.length})
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1.75, backgroundColor: theme.palette.background.alt }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search people"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {isPrivate && (
            <Box sx={{ mt: 1.5, p: 1, borderRadius: 1.5, border: `1px solid ${alpha(accent, 0.5)}`, backgroundColor: alpha(accent, 0.08) }}>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Lock sx={{ fontSize: '1rem', color: accent }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Private meeting: end-to-end encrypted
                </Typography>
              </Box>

              {passcode && (
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Typography variant="body2" sx={{ fontFamily: FONT_MONO, letterSpacing: 1 }}>
                    {formatPasscode(passcode)}
                  </Typography>
                  <Tooltip title="Copy">
                    <IconButton size="small" onClick={() => copy(formatPasscode(passcode))}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}

          {isHost && (
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={1.5}>
              <Typography variant="caption" color="text.secondary">
                You are a host.
              </Typography>

              <Button
                size="small"
                variant="contained"
                disabled={busy || unmutedOthers === 0}
                onClick={() => run('mute-all')}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <MicOff />}
                sx={{
                  backgroundColor: accent,
                  color: accentText,
                  fontWeight: 700,
                  borderRadius: 1.5,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: theme.palette.secondary.light, boxShadow: 'none' },
                  '&.Mui-disabled': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
                    color: theme.palette.text.disabled,
                  },
                }}
              >
                Mute everyone{unmutedOthers > 0 ? ` (${unmutedOthers})` : ''}
              </Button>
            </Box>
          )}

          <List
            dense
            sx={{
              mt: 1.5,
              maxHeight: 380,
              overflow: 'auto',
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : theme.palette.background.default,
            }}
          >
            {list.length === 0 && (
              <Typography color="text.secondary" sx={{ p: 1.5, textAlign: 'center' }}>
                Nobody found.
              </Typography>
            )}

            {list.map((p) => {
              const rowIsHost = hostSet.has(String(p.id))
              const canManage = isHost && !p.isLocal

              return (
                <ListItem
                  key={p.id}
                  sx={{ px: 1.25, py: 0.5 }}
                  secondaryAction={
                    canManage ? (
                      <IconButton
                        edge="end"
                        size="small"
                        disabled={busy}
                        onClick={(e) => setMenu({ anchor: e.currentTarget, person: p })}
                        aria-label={`Actions for ${p.name}`}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  <Avatar
                    src={avatarSrc(avatarOf?.(p.id))}
                    sx={{ width: 34, height: 34, fontSize: '0.95rem', ...avatarColor(p.name, theme.palette.mode) }}
                  >
                    {getInitials(p.name)}
                  </Avatar>

                  <Box display="flex" alignItems="center" gap={0.75} minWidth={0} flex={1} ml={1.25}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {p.isLocal ? `${p.name} (You)` : p.name}
                    </Typography>

                    {rowIsHost && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.3,
                          px: 0.75,
                          borderRadius: '999px',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: accent,
                          border: `1px solid ${alpha(accent, 0.6)}`,
                          flexShrink: 0,
                        }}
                      >
                        <AdminPanelSettings sx={{ fontSize: '0.8rem' }} />
                        Host
                      </Box>
                    )}
                  </Box>

                  {p.hand && <PanTool sx={{ fontSize: '1rem', color: accent, mr: 0.75 }} />}
                  {p.mic ? (
                    <Mic sx={{ fontSize: '1rem', opacity: 0.6, mr: canManage ? 0.5 : 0 }} />
                  ) : (
                    <MicOff sx={{ fontSize: '1rem', color: theme.palette.error.main, mr: canManage ? 0.5 : 0 }} />
                  )}
                </ListItem>
              )
            })}
          </List>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={onClose} sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions for one person */}
      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: theme.zIndex.modal + 70 }}
      >
        <MenuItem
          disabled={!menuPerson?.mic}
          onClick={() => {
            const id = menuPerson.id
            setMenu(null)
            run('mute', id)
          }}
        >
          <ListItemIcon><MicOff fontSize="small" /></ListItemIcon>
          <ListItemText>Mute</ListItemText>
        </MenuItem>

        {!menuIsHost && (
          <MenuItem
            onClick={() => {
              const id = menuPerson.id
              setMenu(null)
              run('make-host', id)
            }}
          >
            <ListItemIcon><AdminPanelSettings fontSize="small" /></ListItemIcon>
            <ListItemText>Make host</ListItemText>
          </MenuItem>
        )}

        {!menuIsHost && (
          <MenuItem
            sx={{ color: theme.palette.error.main }}
            onClick={() => {
              setConfirm(menuPerson)
              setMenu(null)
            }}
          >
            <ListItemIcon><RemoveCircleOutline fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Remove from meeting</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Confirm removal */}
      <Dialog
        open={Boolean(confirm)}
        onClose={() => !busy && setConfirm(null)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: theme.zIndex.modal + 70 }}
        PaperProps={{ sx: paperSx }}
      >
        <DialogTitle sx={{ px: 2, py: 1.5, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
          Remove from meeting?
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1.75, backgroundColor: theme.palette.background.alt }}>
          <Typography variant="body2" sx={{ mt: 1.5, color: theme.palette.text.secondary }}>
            <b style={{ color: theme.palette.text.primary }}>{confirm?.name}</b> will be disconnected and can not
            rejoin this meeting.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setConfirm(null)} disabled={busy} sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={async () => {
              const res = await run('remove', confirm.id)
              if (res?.ok) setConfirm(null)
            }}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <RemoveCircleOutline />}
            sx={{
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastText,
              fontWeight: 700,
              borderRadius: 1.5,
              boxShadow: 'none',
              '&:hover': { backgroundColor: theme.palette.error.dark, boxShadow: 'none' },
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// Visible to everybody while the meeting is recorded
const RecPill = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      px: 0.75,
      py: 0.1,
      borderRadius: '999px',
      fontSize: '0.65rem',
      fontWeight: 700,
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
      color: '#ffffff',
      backgroundColor: 'rgba(211,47,47,0.92)',
      '@keyframes recBlink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
    }}
  >
    <FiberManualRecord sx={{ fontSize: '0.7rem', animation: 'recBlink 1.4s infinite' }} />
    REC
  </Box>
)

const PrivatePill = ({ accent }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      px: 0.75,
      py: 0.1,
      borderRadius: '999px',
      fontSize: '0.65rem',
      fontWeight: 700,
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
      color: accent,
      border: `1px solid ${alpha(accent, 0.7)}`,
      backgroundColor: alpha(accent, 0.12),
    }}
  >
    <Lock sx={{ fontSize: '0.8rem' }} />
    Private
  </Box>
)

const CallDialog = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { auth } = useAuthContext()
  const myId = auth?._id ? String(auth._id) : ''

  const {
    call, localStream, remoteStream, muted, cameraOff, remoteCamera,
    participants, peerStreams, peerMedia, incomingGroup,
    cameras, cameraId, switchCamera,
    mics, micId, switchMic, refreshDevices,
    acceptCall, rejectCall, hangUp, toggleMute, toggleCamera,
    joinGroupCall, dismissIncomingGroup,
    startScreenShare, stopScreenShare, screenSharing, remoteScreenSharing,
    speaking, handRaised, toggleHand, sfu,
    captionsOn, captionsSupported, captions, captionLang, toggleCaptions, changeCaptionLang, captionsActive,
    hosts, hostAction,
    recording, canRecord, isPrivate, passcode
  } = useCall()

  const accent = theme.palette.secondary.main
  const accentText = theme.palette.getContrastText(accent)

  const [seconds, setSeconds] = useState(0)
  const [size, setSize] = useState('small')      // when video is showing: 'bar' | 'small' | 'large'
  const [pos, setPos] = useState(null)           // null = default (top center), else { x, y }
  const dragRef = useRef(null)
  const prevSizeRef = useRef('small')   // size to go back to when leaving full screen
  const [cameraMenuAnchor, setCameraMenuAnchor] = useState(null)
  const [micMenuAnchor, setMicMenuAnchor] = useState(null)
  const [chatRect, setChatRect] = useState(null) // last known size/position of the Chat page area
  const { users } = useUserContext()
  const [captionMenuAnchor, setCaptionMenuAnchor] = useState(null)
  const [peopleOpen, setPeopleOpen] = useState(false)

  const isGroup = call?.kind === 'group'
  const isSfu = call?.mode === 'sfu'
  const ringing = call?.status === 'ringing'
  const cameraOn = !cameraOff
  const captionMap = captions || {}
  const captionCount = captionsOn ? Object.keys(captionMap).length : 0

  const others = isGroup && !isSfu ? participants.filter((p) => p.userId !== myId) : []
  
  // userId -> avatar, from the call's participant list (includes me)
  const avatarById = useMemo(
    () => Object.fromEntries((participants || []).map((p) => [String(p.userId), p.avatar || null])),
    [participants]
  )
  const avatarOf = (id) => avatarById[String(id)] || null

  // 1:1: the other person's picture
  const peerAvatar =
    call?.peerAvatar ||
    users?.find((u) => String(u._id) === String(call?.peerId))?.avatar ||
    null

  const sfuPeople = isSfu ? (sfu?.participants || []) : []
  const sfuPresenter = sfuPeople.find((p) => p.screen && !p.isLocal) || null
  const isHost = (hosts || []).includes(myId)

  const mySpeaking = isSfu
    ? Boolean(sfuPeople.find((p) => p.isLocal)?.speaking)
    : Boolean(speaking.me)
  const remoteSpeaking = isSfu
    ? sfuPeople.some((p) => !p.isLocal && p.speaking)
    : isGroup
    ? others.some((p) => speaking[p.userId])
    : Boolean(call && speaking[call.peerId])

  // Who (other than me) is presenting their screen right now?
  const presenter = isSfu
    ? (sfuPresenter ? { userId: sfuPresenter.id, name: sfuPresenter.name } : null)
    : isGroup
    ? others.find((p) => peerMedia[p.userId]?.screenShare) || null
    : (remoteScreenSharing && call ? { userId: call.peerId, name: call.peerName } : null)
  const someonePresenting = Boolean(presenter)

  // My camera is only visible to others when I am not presenting (the video slot carries the screen)
  const myCameraVisible = cameraOn && !screenSharing

  const anyRemoteCamera = isGroup ? others.some((p) => peerMedia[p.userId]?.camera) : remoteCamera

  // A large meeting always shows the people grid. Otherwise the video layout starts when
  // ANYONE has a camera on or is presenting.
  const videoActive =
    Boolean(call) && !ringing && (isSfu || myCameraVisible || anyRemoteCamera || someonePresenting)

    // New call: reset size and position
  useEffect(() => {
    setSize('small')
    setPos(null)
  }, [call?.callId])

  // No video any more: next time video starts, show it normally
  useEffect(() => {
    if (!videoActive) setSize('small')
  }, [videoActive])

  // Someone started presenting: show it big (only when there is a chat page area to match)
  useEffect(() => {
    if (someonePresenting && document.getElementById(CHAT_ROOT_ID)) {
      setSize((s) => (s === 'full' ? s : 'large'))
    }
  }, [someonePresenting])

  // Call timer
  useEffect(() => {
    if (call?.status !== 'connected') {
      setSeconds(0)
      return
    }
    const tick = () => setSeconds(Math.max(0, Math.floor((Date.now() - call.connectedAt) / 1000)))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [call?.status, call?.connectedAt])

  // Leaving full screen (any way) also leaves the browser's full screen
  useEffect(() => {
    if (size !== 'full' && document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {})
    }
  }, [size])

  // The browser left full screen (Esc key etc.): leave full screen mode too
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) {
        setSize((s) => (s === 'full' ? prevSizeRef.current : s))
      }
    }
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  // Esc also works when the browser refused real full screen (CSS-only mode)
  useEffect(() => {
    if (size !== 'full') return
    const onKey = (e) => {
      if (e.key === 'Escape') setSize(prevSizeRef.current)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [size])

  // Never leave the browser stuck in full screen if this component goes away
  useEffect(() => () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {})
  }, [])

  // ---------- Match the Chat page area while expanded ----------
  const measureChat = useCallback(() => {
    const el = document.getElementById(CHAT_ROOT_ID)
    if (!el) return                     // not on the chat page: keep the last known area

    const r = el.getBoundingClientRect()
    const next = {
      left: Math.round(r.left),
      top: Math.round(r.top),
      width: Math.round(r.width),
      height: Math.round(r.height),
    }

    setChatRect((prev) =>
      prev &&
      prev.left === next.left &&
      prev.top === next.top &&
      prev.width === next.width &&
      prev.height === next.height
        ? prev
        : next
    )
  }, [])

  const wantsExpanded = size === 'large'

  useEffect(() => {
    if (!wantsExpanded) return

    measureChat()
    window.addEventListener('resize', measureChat)

    const el = document.getElementById(CHAT_ROOT_ID)
    const observer = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureChat) : null
    if (observer) observer.observe(el)

    // Catches the sidebar opening/closing and the chat page mounting after navigation
    const timer = setInterval(measureChat, 250)

    return () => {
      window.removeEventListener('resize', measureChat)
      if (observer) observer.disconnect()
      clearInterval(timer)
    }
  }, [wantsExpanded, measureChat])

  // ---------- Button styles ----------
  const roundBtn = (bg, dim = 38) => ({
    width: dim,
    height: dim,
    color: '#ffffff',
    backgroundColor: bg,
    '&:hover': { backgroundColor: bg, filter: 'brightness(1.15)' },
  })

  // active = the "special" state (muted / camera off), shown brighter
  const softBtn = (active) => ({
    width: 38,
    height: 38,
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.text.primary, active ? 0.3 : 0.12),
    '&:hover': { backgroundColor: alpha(theme.palette.text.primary, active ? 0.4 : 0.2) },
    '&.Mui-disabled': { color: theme.palette.text.disabled },
  })

  const pulseAvatarSx = (on, talking = false) => ({
    width: 40,
    height: 40,
    flexShrink: 0,
    bgcolor: theme.palette.primary.main,
    color: '#ffffff',
    boxShadow: talking ? `0 0 0 3px ${accent}` : 'none',
    transition: 'box-shadow 0.15s ease',
    '@keyframes callPulse': {
      '0%': { boxShadow: `0 0 0 0 ${alpha(accent, 0.6)}` },
      '70%': { boxShadow: `0 0 0 12px ${alpha(accent, 0)}` },
      '100%': { boxShadow: `0 0 0 0 ${alpha(accent, 0)}` },
    },
    animation: on ? 'callPulse 1.6s infinite' : 'none',
  })

  const framePaperSx = {
    position: 'fixed',
    zIndex: theme.zIndex.modal + 50,     // above page content and dialogs, below notifications
    overflow: 'hidden',
    borderRadius: 2,
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.text.primary,
    border: `1px solid ${alpha(accent, 0.5)}`,
    userSelect: 'none',
  }

  // ---------- Someone started a group call: ring card (no call of mine yet) ----------
  if (!call) {
    if (!incomingGroup) return null

    const isVid = incomingGroup.type === 'video'

    return (
      <Paper
        elevation={10}
        sx={{ ...framePaperSx, left: '50%', top: 16, transform: 'translateX(-50%)', width: 420, maxWidth: 'calc(100vw - 32px)' }}
      >
        <Box display="flex" alignItems="center" gap={1.25} p={1.25}>
          <Avatar sx={pulseAvatarSx(true)}>
            <Groups />
          </Avatar>

          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
              {incomingGroup.groupName}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: accent, display: 'block' }}>
              {incomingGroup.fromName} started a {isVid ? 'video' : 'audio'} call
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.75} alignItems="center">
            <Tooltip title="Dismiss">
              <IconButton onClick={dismissIncomingGroup} sx={roundBtn(theme.palette.error.main, 42)}>
                <CallEnd fontSize="small" />
              </IconButton>
            </Tooltip>

            {isVid && (
              <Tooltip title="Join with audio only">
                <IconButton onClick={() => joinGroupCall(incomingGroup, false)} sx={roundBtn(theme.palette.success.main, 42)}>
                  <Call fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={isVid ? 'Join with video' : 'Join'}>
              <IconButton onClick={() => joinGroupCall(incomingGroup)} sx={roundBtn(theme.palette.success.main, 42)}>
                {isVid ? <Videocam fontSize="small" /> : <Call fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>
    )
  }

  const dialing = call.status === 'calling'
  const connected = call.status === 'connected'
  const canToggleCamera = isSfu ? connected : (call.status === 'connecting' || connected)
  const showVideo = videoActive && size !== 'bar'
  const full = showVideo && size === 'full'
  const expanded = showVideo && (size === 'large' || size === 'full')   // big layout, no dragging
  const initial = getInitials(call.peerName)

  const title = isGroup ? call.groupName : call.peerName
  const chatId = isGroup ? call.groupId : call.peerId

  const statusText =
    ringing ? `Incoming ${call.type} call`
    : dialing ? 'Calling…'
    : call.status === 'connecting' ? 'Connecting…'
    : call.reconnecting ? 'Reconnecting…'
    : isGroup ? `${participants.length} in call · ${formatDuration(seconds)}${captionsActive ? ' · CC on' : ''}`
    : `${formatDuration(seconds)}${captionsActive ? ' · CC on' : ''}`

  const goToChat = () => {
    requestOpenChat(chatId)
    navigate('/chat')
  }

  // Expand = same size as the Chat page. If we are on another page, go to the chat first
  const toggleExpand = () => {
    if (size === 'large') {
      setSize('small')
      return
    }
    if (!document.getElementById(CHAT_ROOT_ID)) goToChat()
    setSize('large')
  }

  const enterFull = () => {
    prevSizeRef.current = size === 'full' ? 'small' : size
    setSize('full')

    // Must run inside the click handler. If it fails, the CSS full-viewport mode still works
    const el = document.documentElement
    if (!document.fullscreenElement && el.requestFullscreen) {
      el.requestFullscreen().catch(() => {})
    }
  }

  const exitFull = () => setSize(prevSizeRef.current)

  // ---------- Dragging (not while expanded) ----------
  const onPointerDown = (e) => {
    if (expanded) return
    if (!e.currentTarget.contains(e.target)) return   // events coming from the device menus
    if (e.target.closest('button')) return            // let the buttons work normally
    const rect = e.currentTarget.getBoundingClientRect()
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e) => {
    const d = dragRef.current
    if (!d) return

    const w = e.currentTarget.offsetWidth
    const h = e.currentTarget.offsetHeight
    const x = Math.min(Math.max(0, e.clientX - d.dx), Math.max(0, window.innerWidth - w))
    const y = Math.min(Math.max(0, e.clientY - d.dy), Math.max(0, window.innerHeight - h))
    setPos({ x, y })
  }

  const onPointerUp = (e) => {
    dragRef.current = null
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }

  const incomingVideo = call.type === 'video'

  // ---------- Screen share button ----------
  const canShareScreen =
    typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia)

  const shareDisabled = !canShareScreen || !connected || (someonePresenting && !screenSharing)

  const shareTitle =
    !canShareScreen ? 'Screen sharing is not supported in this browser'
    : !connected ? 'Available once connected'
    : screenSharing ? 'Stop sharing'
    : someonePresenting ? `${presenter.name} is presenting`
    : 'Share screen'

  const shareBtnSx = screenSharing
    ? {
        ...softBtn(true),
        backgroundColor: accent,
        color: accentText,
        '&:hover': { backgroundColor: theme.palette.secondary.light },
      }
    : softBtn(false)

  const controls = ringing ? (
    <>
      <Tooltip title="Decline">
        <IconButton onClick={rejectCall} sx={roundBtn(theme.palette.error.main, 42)}>
          <CallEnd fontSize="small" />
        </IconButton>
      </Tooltip>

      {incomingVideo && (
        <Tooltip title="Accept with audio only">
          <IconButton onClick={() => acceptCall(false)} sx={roundBtn(theme.palette.success.main, 42)}>
            <Call fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title={incomingVideo ? 'Accept with video' : 'Accept'}>
        <IconButton onClick={() => acceptCall(incomingVideo)} sx={roundBtn(theme.palette.success.main, 42)}>
          {incomingVideo ? <Videocam fontSize="small" /> : <Call fontSize="small" />}
        </IconButton>
      </Tooltip>
    </>
  ) : (
    <>
      {videoActive && size === 'bar' && (
        <Tooltip title="Show video">
          <IconButton onClick={() => setSize('small')} sx={softBtn(false)}>
            <ExpandLess fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title={expanded ? 'Back to chat' : 'Open chat'}>
        <IconButton
          onClick={() => {
            if (expanded) setSize('small')     // the expanded call covers the chat: shrink it first
            goToChat()
          }}
          sx={softBtn(false)}
        >
          <ChatBubbleOutline fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={muted ? 'Unmute' : 'Mute'}>
          <IconButton onClick={toggleMute} sx={softBtn(muted)}>
            {muted ? <MicOff fontSize="small" /> : <Mic fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Choose microphone">
          <IconButton
            size="small"
            onClick={(e) => {
              refreshDevices()
              setMicMenuAnchor(e.currentTarget)
            }}
            sx={{ width: 22, height: 38, borderRadius: 1, color: theme.palette.text.secondary }}
          >
            <ArrowDropDown fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip
          title={
            !canToggleCamera ? 'Camera is available once connected'
            : cameraOn ? 'Turn camera off'
            : 'Turn camera on'
          }
        >
          <span>
            <IconButton onClick={toggleCamera} disabled={!canToggleCamera} sx={softBtn(!cameraOn)}>
              {cameraOn ? <Videocam fontSize="small" /> : <VideocamOff fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Choose camera">
          <IconButton
            size="small"
            onClick={(e) => {
              refreshDevices()
              setCameraMenuAnchor(e.currentTarget)
            }}
            sx={{ width: 22, height: 38, borderRadius: 1, color: theme.palette.text.secondary }}
          >
            <ArrowDropDown fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {isGroup && connected && (
        <Tooltip title={handRaised ? 'Lower hand' : 'Raise hand'}>
          <IconButton
            onClick={toggleHand}
            sx={
              handRaised
                ? { ...softBtn(true), backgroundColor: accent, color: accentText, '&:hover': { backgroundColor: theme.palette.secondary.light } }
                : softBtn(false)
            }
          >
            <PanTool fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {connected && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip
            title={
              isPrivate ? 'Captions are off in private meetings'
              : !captionsSupported ? 'Live captions need Chrome or Edge'
              : captionsOn ? 'Turn captions off'
              : 'Turn captions on'
            }
          >
            <span>
              <IconButton
                onClick={toggleCaptions}
                disabled={!captionsSupported}
                sx={
                  captionsOn
                    ? { ...softBtn(true), backgroundColor: accent, color: accentText, '&:hover': { backgroundColor: theme.palette.secondary.light } }
                    : softBtn(false)
                }
              >
                {captionsOn ? <ClosedCaption fontSize="small" /> : <ClosedCaptionOff fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Language I speak">
            <span>
              <IconButton
                size="small"
                disabled={!captionsSupported}
                onClick={(e) => setCaptionMenuAnchor(e.currentTarget)}
                sx={{ width: 22, height: 38, borderRadius: 1, color: theme.palette.text.secondary }}
              >
                <ArrowDropDown fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      
      {isSfu && connected && (
        <Tooltip title={isHost ? 'People (you are a host)' : 'People'}>
          <IconButton onClick={() => setPeopleOpen(true)} sx={softBtn(false)}>
            <Badge badgeContent={sfuPeople.length} max={999} color="secondary">
              <People fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
      )}      

      {isSfu && connected && isHost && canRecord && (
        <Tooltip title={recording ? 'Stop recording' : 'Record the meeting (audio only)'}>
          <IconButton
            onClick={() => {
              if (recording) {
                hostAction('record-stop')
                return
              }
              if (window.confirm('Start recording this meeting? The audio of everyone in the meeting will be recorded, and everyone will be told.')) {
                hostAction('record-start')
              }
            }}
            sx={
              recording
                ? { ...softBtn(true), backgroundColor: theme.palette.error.main, color: '#ffffff', '&:hover': { backgroundColor: theme.palette.error.dark } }
                : softBtn(false)
            }
          >
            <FiberManualRecord fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      
      <Tooltip title={shareTitle}>
        <span>
          <IconButton
            onClick={screenSharing ? stopScreenShare : startScreenShare}
            disabled={shareDisabled}
            sx={shareBtnSx}
          >
            {screenSharing ? <StopScreenShare fontSize="small" /> : <ScreenShare fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={isGroup ? 'Leave call' : 'End call'}>
        <IconButton onClick={() => hangUp('ended')} sx={roundBtn(theme.palette.error.main)}>
          <CallEnd fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  )

  // ---------- Window frame ----------
  const width = showVideo ? (isGroup ? 560 : 480) : (ringing ? 380 : 560)

  const frameSx = full
    ? {
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        maxWidth: 'none',
        transform: 'none',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        border: 'none',
        cursor: 'default',
        touchAction: 'auto',
      }
    : expanded
    ? {
        // Same box as the Chat page (falls back to "almost everything" until it is measured)
        left: chatRect?.left ?? 24,
        top: chatRect?.top ?? 80,
        width: chatRect?.width ?? 'calc(100vw - 48px)',
        height: chatRect?.height ?? 'calc(100vh - 104px)',
        maxWidth: 'none',
        transform: 'none',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'default',
        touchAction: 'auto',
      }
    : {
        ...(pos
          ? { left: pos.x, top: pos.y }
          : { left: '50%', top: 16, transform: 'translateX(-50%)' }),
        width,
        maxWidth: 'calc(100vw - 32px)',
        cursor: 'grab',
        touchAction: 'none',
        '&:active': { cursor: 'grabbing' },
      }
      
  // Group tiles: everyone else, then me. The video of a presenter IS their screen,
  // so their camera picture is not shown while they present.
  const tiles = isGroup && !isSfu
    ? [
        ...others.map((p) => {
          const m = peerMedia[p.userId] || {}
          return {
            key: p.userId,
            name: p.name,
            avatar: p.avatar,
            stream: peerStreams[p.userId],
            camera: Boolean(m.camera) && !m.screenShare,
            muted: Boolean(m.muted),
            mirror: false,
            presenting: Boolean(m.screenShare),
            speaking: Boolean(speaking[p.userId]),
            hand: Boolean(m.handRaised)
          }
        }),
        {
          key: 'me',
          name: 'You',
          avatar: avatarOf(myId),
          stream: localStream,
          camera: myCameraVisible,
          muted,
          mirror: true,
          presenting: screenSharing,
          speaking: mySpeaking,
          hand: handRaised
        },
      ]
    : []

  const cols = tiles.length <= 1 ? 1 : tiles.length <= 4 ? 2 : 3
  const rows = Math.ceil(tiles.length / cols)

  // "X is presenting" label over the big picture
  const presentingLabel = presenter && (
    <Box
      sx={{
        position: 'absolute',
        left: 8,
        bottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.9,
        py: 0.3,
        borderRadius: 1,
        backgroundColor: 'rgba(0,0,0,0.6)',
      }}
    >
      <ScreenShare sx={{ fontSize: '1rem', color: accent }} />
      <Typography variant="caption" noWrap sx={{ color: '#ffffff', fontWeight: 600 }}>
        {presenter.name} is presenting
      </Typography>
    </Box>
  )

  return (
    <>
      {/* Sound: one audio element per remote participant */}
      {isGroup
        ? others.map((p) => <RemoteAudio key={p.userId} stream={peerStreams[p.userId]} />)
        : <RemoteAudio stream={remoteStream} />}

      <Paper
        elevation={10}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          ...framePaperSx,
          transition: expanded ? 'none' : 'width 0.2s ease',
          ...frameSx,
        }}
      >
        {showVideo ? (
          <>
            <Box
              sx={{
                position: 'relative',
                backgroundColor: '#000000',
                ...(expanded
                  ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
                  : isGroup ? {} : { aspectRatio: '16 / 9' }),
              }}
            >
              {isSfu ? (
                <SfuStage people={sfuPeople} expanded={expanded} avatarOf={avatarOf} />
              ) : isGroup ? (
                presenter ? (
                  // Group, someone is presenting: the screen on a big stage, everyone in a strip below
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      p: 0.5,
                      pt: expanded ? 7 : 5.5,            // room for the name / buttons on top
                      ...(expanded ? { flex: 1, minHeight: 0 } : {}),
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 1,
                        backgroundColor: '#111111',
                        ...(expanded ? { flex: 1, minHeight: 0 } : { aspectRatio: '16 / 9' }),
                      }}
                    >
                      <VideoEl
                        stream={peerStreams[presenter.userId]}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                      />
                      {presentingLabel}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', flexShrink: 0 }}>
                      {tiles.map((t) => (
                        <Box key={t.key} sx={{ width: expanded ? 160 : 100, flexShrink: 0 }}>
                          <GroupTile
                            name={t.name}
                            avatar={t.avatar}
                            stream={t.stream}
                            camera={t.camera}
                            muted={t.muted}
                            mirror={t.mirror}
                            presenting={t.presenting}
                            speaking={t.speaking}
                            hand={t.hand}
                            expanded={false}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  // Group, nobody is presenting: a tile for everyone
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 0.5,
                      p: 0.5,
                      pt: expanded ? 7 : 5.5,            // room for the name / buttons on top
                      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                      ...(expanded
                        ? { flex: 1, minHeight: 0, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }
                        : {}),
                    }}
                  >
                    {tiles.map((t) => (
                      <GroupTile
                        key={t.key}
                        name={t.name}
                        avatar={t.avatar}
                        stream={t.stream}
                        camera={t.camera}
                        muted={t.muted}
                        mirror={t.mirror}
                        presenting={t.presenting}
                        speaking={t.speaking}
                        hand={t.hand}
                        expanded={expanded}
                      />
                    ))}
                  </Box>
                )
              ) : (
                <>
                  {/* 1:1: the other person's camera OR their screen, or the avatar if neither is on */}
                  {(remoteCamera || remoteScreenSharing) && remoteStream ? (
                    <VideoEl
                      stream={remoteStream}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: expanded || remoteScreenSharing ? 'contain' : 'cover',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                      <Avatar
                        src={avatarSrc(peerAvatar)}
                        sx={{
                          width: expanded ? 120 : 72,
                          height: expanded ? 120 : 72,
                          fontSize: expanded ? '3.2rem' : '2rem',
                          bgcolor: theme.palette.primary.main,
                        }}
                      >
                        {initial}
                      </Avatar>
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      border: `3px solid ${accent}`,
                      opacity: remoteSpeaking ? 1 : 0,
                      transition: 'opacity 0.15s ease',
                    }}
                  />

                  {presentingLabel}

                  {/* My preview (not while I present: the video slot carries my screen) */}
                  {localStream && myCameraVisible && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 12,
                        bottom: 12,
                        width: expanded ? 220 : 96,
                        aspectRatio: '4 / 3',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: `2px solid ${mySpeaking ? accent : alpha('#ffffff', 0.5)}`,
                        backgroundColor: '#111111',
                      }}
                    >
                      <VideoEl
                        stream={localStream}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transform: 'scaleX(-1)',
                          pointerEvents: 'none',
                        }}
                      />
                    </Box>
                  )}
                </>
              )}

              {/* Name, timer and size buttons */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  p: expanded ? 1.5 : 1,
                  background: 'linear-gradient(rgba(0,0,0,0.65), transparent)',
                }}
              >
                <Box minWidth={0}>
                  <Typography
                    variant={expanded ? 'h6' : 'subtitle2'}
                    noWrap
                    sx={{ color: '#ffffff', fontWeight: 700 }}
                  >
                    {title}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Typography variant="caption" sx={{ color: '#ffffff', fontVariantNumeric: 'tabular-nums' }}>
                      {statusText}
                    </Typography>
                    {screenSharing && <PresentingPill accent={accent} />}
                    {recording && <RecPill />}
                    {isPrivate && <PrivatePill accent={accent} />}
                  </Box>
                </Box>

                <Stack direction="row" spacing={0.5}>
                  {full ? (
                    <Tooltip title="Exit full screen">
                      <IconButton size="small" onClick={exitFull} sx={{ color: '#ffffff' }}>
                        <FullscreenExit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title={expanded ? 'Back to small window' : 'Expand to chat size'}>
                        <IconButton size="small" onClick={toggleExpand} sx={{ color: '#ffffff' }}>
                          {expanded ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Full screen">
                        <IconButton size="small" onClick={enterFull} sx={{ color: '#ffffff' }}>
                          <Fullscreen fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Minimize">
                        <IconButton size="small" onClick={() => setSize('bar')} sx={{ color: '#ffffff' }}>
                          <ExpandMore fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Box>

              {/* Live captions */}
              {captionCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 8,
                    right: 8,
                    bottom: expanded ? 20 : 8,
                    zIndex: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <CaptionLines captions={captionMap} myId={myId} large={expanded} />
                </Box>
              )}

            </Box>

            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              sx={{
                flexShrink: 0,
                flexWrap: 'wrap',                 // wrap instead of being cut off
                gap: expanded ? 2 : 1.25,         // replaces spacing: it breaks when items wrap
                px: 1,
                py: expanded ? 1.75 : 1.25,
              }}
            >
              {controls}
            </Stack>
          </>
        ) : (
          // Compact bar: incoming call, audio call, minimized video, or "I am presenting"
          <>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.25} p={1.25}>          
              <Avatar
                src={isGroup ? undefined : avatarSrc(peerAvatar)}
                sx={pulseAvatarSx(ringing || dialing, remoteSpeaking)}
              >
                {isGroup ? <Groups /> : initial}
              </Avatar>

              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                  {title}
                </Typography>

                <Box display="flex" alignItems="center" gap={0.75}>
                  {connected && (
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        flexShrink: 0,
                        backgroundColor: theme.palette.success.main,
                      }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: ringing ? accent : theme.palette.text.secondary,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {statusText}
                  </Typography>
                  {screenSharing && <PresentingPill accent={accent} />}
                  {recording && <RecPill />}
                  {isPrivate && <PrivatePill accent={accent} />}
                </Box>
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  flexWrap: 'wrap',
                  gap: 0.75,
                  justifyContent: ringing ? 'flex-end' : 'center',
                  ...(ringing ? {} : { width: '100%' }),   // connected: the buttons get their own row
                }}
              >
                {controls}
              </Stack>
            </Box>

            {captionCount > 0 && !ringing && (
              <Box px={1.25} pb={1.25}>
                <CaptionLines captions={captionMap} myId={myId} />
              </Box>
            )}            
          </>
        )}
      </Paper>

      <Menu
        anchorEl={cameraMenuAnchor}
        open={Boolean(cameraMenuAnchor)}
        onClose={() => setCameraMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: theme.zIndex.modal + 60 }}    // above the floating call card
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.5, display: 'block', color: theme.palette.text.secondary }}
        >
          Camera
        </Typography>

        {cameras.length === 0 && <MenuItem disabled>No camera found</MenuItem>}

        {cameras.map((cam) => (
          <MenuItem
            key={cam.deviceId || cam.label}
            selected={cam.deviceId === cameraId}
            onClick={() => {
              setCameraMenuAnchor(null)
              switchCamera(cam.deviceId)
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {cam.deviceId === cameraId ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{cam.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={micMenuAnchor}
        open={Boolean(micMenuAnchor)}
        onClose={() => setMicMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: theme.zIndex.modal + 60 }}    // above the floating call card
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.5, display: 'block', color: theme.palette.text.secondary }}
        >
          Microphone
        </Typography>

        {mics.length === 0 && <MenuItem disabled>No microphone found</MenuItem>}

        {mics.map((mic) => (
          <MenuItem
            key={mic.deviceId || mic.label}
            selected={mic.deviceId === micId}
            onClick={() => {
              setMicMenuAnchor(null)
              switchMic(mic.deviceId)
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {mic.deviceId === micId ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{mic.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={captionMenuAnchor}
        open={Boolean(captionMenuAnchor)}
        onClose={() => setCaptionMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: theme.zIndex.modal + 60 }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: theme.palette.text.secondary }}>
          Language I speak
        </Typography>

        {CAPTION_LANGUAGES.map((l) => (
          <MenuItem
            key={l.code}
            selected={l.code === captionLang}
            onClick={() => {
              setCaptionMenuAnchor(null)
              changeCaptionLang(l.code)
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {l.code === captionLang ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{l.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {isSfu && (
        <ParticipantsDialog
          open={peopleOpen}
          onClose={() => setPeopleOpen(false)}
          people={sfuPeople}
          hostIds={hosts}
          isHost={isHost}
          avatarOf={avatarOf}
          onAction={hostAction}
          isPrivate={isPrivate} 
          passcode={passcode}
        />
      )}
    </>
  )
}

export default CallDialog